import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import { ParticipantModel } from "models/ParticipantModel";
import { useLayout, useTranslations } from "providers";
import { useEditParticipant } from "providers";
import { useParticipant } from "providers";
import React, { PropsWithChildren } from "react";
import { Item, Menu, TriggerEvent, useContextMenu } from "react-contexify";

import { ParticipantsContextMenuContext } from "./ParticipantsContextMenuContext";

const menuId = "participants-context-menu";

const logger = createLogger("ParticipantsContextMenuProvider");

export const ParticipantsContextMenuProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { setSelectParticipantId, setConfirmDeleteParticipant } = useParticipant();
  const { setEditParticipantId } = useEditParticipant();
  const { show } = useContextMenu({ id: menuId });
  const { handleNavClick } = useLayout();

  function onClickDelete(item: ParticipantModel) {
    logger.debug("onClickDelete(): ", item);
    setConfirmDeleteParticipant({ id: item.id, title: item.user.displayName });
  }

  function showContextMenu(event: TriggerEvent, item: ParticipantModel) {
    logger.debug("showContextMenu(): ", item);
    setSelectParticipantId(item.id);
    show({ event, id: menuId, props: item });
  }

  function onClickViewHistory(item: ParticipantModel): void {
    logger.debug("onClickViewHistory(): ", item);
    const url = AppRoutes.toBackendAuditLog("Participant", item.id.toString());
    handleNavClick(url);
  }

  function onClickEdit(item: ParticipantModel): void {
    logger.debug("onClickEdit(): ", item);
    setEditParticipantId(item.id);
  }

  function isDisabledEdit(item: ParticipantModel): boolean {
    return !item.canEdit;
  }

  function isDisabledViewHistory(item: ParticipantModel) {
    if (!item) return true;
    return !item.canViewHistory;
  }

  function isDisabledDelete(item: ParticipantModel) {
    if (!item) return true;
    return !item.canDelete;
  }

  return (
    <ParticipantsContextMenuContext.Provider
      value={{
        showContextMenu,
      }}
    >
      <Menu id={menuId} theme="sbb">
        <Item onClick={(e) => onClickEdit(e.props)} disabled={(e) => isDisabledEdit(e.props)}>
          {i18n.Actions_Edit_Participant}
        </Item>
        <Item onClick={(e) => onClickDelete(e.props)} disabled={(e) => isDisabledDelete(e.props)}>
          {i18n.Actions_Delete_Participant}
        </Item>
        <Item onClick={(e) => onClickViewHistory(e.props)} disabled={(e) => isDisabledViewHistory(e.props)}>
          {i18n.Actions_View_History}
        </Item>
      </Menu>

      {children}
    </ParticipantsContextMenuContext.Provider>
  );
};
